import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { institutionApi } from '../../../api/institutionsApi';
import NlimsDataTable from '../../shared/NlimsDataTable/NlimsDataTable';
import CreateInstitutionModal from '../Modals/CreateInstitutionModal';
import EditInstitutionModal from '../Modals/EditInstitutionModal';
import RecordDetailsModal from '../shared/RecordDetailsModal';
import { INSTITUTION_TYPES } from '../data/enums';
import { useLocationOptions } from '../shared/useLocationOptions';
import SystemRowMenu, { standardRowItems, confirmAction } from '../shared/SystemRowMenu';
import { anchorRect } from '../../shared/useAnchoredMenu';

const STATUS_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

function humanize(value) {
  if (!value) return '-';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusText(value) {
  return value ? 'Active' : 'Inactive';
}

function statusCellHtml(data, type) {
  const active = Boolean(data);
  const label = statusText(active);

  if (type === 'sort' || type === 'type') {
    return active ? 1 : 0;
  }

  if (type !== 'display') {
    return label;
  }

  const pillStyle = active
    ? 'background:#dcfce7;color:#15803d;border:1px solid #86efac;'
    : 'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;';

  return `
    <button
      type="button"
      class="nlims-action-btn"
      data-action="toggle-status"
      title="Toggle status"
      style="display:inline-flex;align-items:center;gap:6px;padding:2px 10px;border-radius:999px;font-size:11px;font-weight:700;line-height:1.4;cursor:pointer;${pillStyle}"
    >
      ${label}
    </button>
  `;
}

function institutionTypeCell(value, type) {
  if (type === 'sort' || type === 'type') {
    return value ?? '';
  }
  return humanize(value);
}

function stateCell(value, type) {
  if (type === 'sort' || type === 'type') {
    return value ?? '';
  }
  return value || '-';
}

const ACTION_RENDER = (_data, type) => {
  if (type !== 'display') return '';
  return `<button type="button" class="nlims-action-btn" data-action="menu" style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:3px 12px;cursor:pointer;color:#15803d;font-weight:800;font-size:15px;letter-spacing:2px;line-height:1.4;">&#8943;</button>`;
};

const COLUMNS = [
  { title: 'Name', data: 'name', sortable: true },
  { title: 'Institution Type', data: 'institution_type', sortable: true, render: institutionTypeCell },
  { title: 'State', data: 'state', sortable: true, render: stateCell },
  { title: 'Status', data: 'is_active', sortable: true, render: statusCellHtml },
  { title: 'Action', data: null, sortable: false, render: ACTION_RENDER },
];

function SearchCreateRow({ search, setSearch, onSearch, onCreate, createLabel = 'Create New' }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <div className="relative flex-1">
        <svg
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          placeholder="Search here"
          className="w-full rounded-lg border border-gray-200 py-2.5 pl-9 pr-4 text-sm text-gray-700 placeholder-gray-400 focus:border-green-400 focus:outline-none focus:ring-1 focus:ring-green-400"
        />
      </div>
      <button
        type="button"
        onClick={onSearch}
        className="rounded-lg border border-green-200 bg-green-50 px-5 py-2.5 text-sm font-semibold text-green-700 hover:bg-green-100"
      >
        Search
      </button>
      <button
        type="button"
        onClick={onCreate}
        className="flex items-center gap-1.5 rounded-lg bg-green-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-800"
      >
        <span className="text-base font-bold leading-none">+</span> {createLabel}
      </button>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="cursor-pointer appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:border-green-400 focus:outline-none"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}

export default function InstitutionsManagement() {
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [stateFilter, setStateFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [refreshTick, setRefreshTick] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [rowMenu, setRowMenu] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const { stateOptions } = useLocationOptions();

  const backendFilters = useMemo(() => {
    const params = {};
    if (typeFilter !== 'All') params.institution_type = typeFilter;
    if (stateFilter !== 'All') params.state = stateFilter;
    if (statusFilter !== 'All') params.is_active = statusFilter === 'Active' ? 1 : 0;
    return params;
  }, [stateFilter, statusFilter, typeFilter]);

  const activeFilters = useMemo(() => {
    const items = [];
    if (typeFilter !== 'All') items.push(`Type: ${humanize(typeFilter)}`);
    if (stateFilter !== 'All') items.push(`State: ${stateFilter}`);
    if (statusFilter !== 'All') items.push(`Status: ${statusFilter}`);
    if (searchQuery) items.push(`Search: ${searchQuery}`);
    return items;
  }, [searchQuery, stateFilter, statusFilter, typeFilter]);

  const handleSearch = () => {
    setSearchQuery(searchDraft.trim());
  };

  const handleCreate = async (payload) => {
    await institutionApi.create(payload);
    setRefreshTick((value) => value + 1);
  };

  const handleSave = async (payload) => {
    if (!payload?.uuid) {
      throw new Error('Missing institution identifier.');
    }
    await institutionApi.update(payload.uuid, payload);
    setRefreshTick((value) => value + 1);
  };

  const handleToggleStatus = async (row) => {
    if (!row?.uuid) return;
    const nextLabel = row.is_active ? 'Deactivate' : 'Activate';
    if (!(await confirmAction({ title: `${nextLabel} Institution?`, message: `${row.name} will be marked ${row.is_active ? 'Inactive' : 'Active'}.`, confirmLabel: nextLabel }))) return;

    try {
      await institutionApi.update(row.uuid, {
        name: row.name,
        institution_type: row.institution_type,
        state: row.state,
        is_active: !row.is_active,
      });
      toast.success(row.is_active ? 'Institution deactivated.' : 'Institution activated.', {
        position: 'top-right',
      });
      setRefreshTick((value) => value + 1);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to update institution status.';
      toast.error(message, { position: 'top-right' });
    }
  };

  const handleDelete = async (row) => {
    if (!row?.uuid) return;
    const confirmed = await confirmAction({ title: 'Delete Record?', message: `Delete "${row.name}"? This will deactivate the institution on the backend.`, confirmLabel: 'Delete', danger: true });
    if (!confirmed) return;

    setDeleting(true);
    try {
      await institutionApi.remove(row.uuid);
      toast.success('Institution removed.', { position: 'top-right' });
      setViewRow(null);
      setRefreshTick((value) => value + 1);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to delete institution.';
      toast.error(message, { position: 'top-right' });
    } finally {
      setDeleting(false);
    }
  };

  const handleRowAction = (action, row, meta) => {
    if (!row) return;
    if (action === 'menu') { setRowMenu({ row, rect: anchorRect(meta?.button) }); return; }
    if (action === 'toggle-status') {
      handleToggleStatus(row);
    }
  };

  return (
    <section>
      <SearchCreateRow
        search={searchDraft}
        setSearch={setSearchDraft}
        onSearch={handleSearch}
        onCreate={() => setCreateOpen(true)}
      />

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <FilterSelect
          label="Filter by Institution Type:"
          value={typeFilter}
          onChange={setTypeFilter}
          options={[{ value: 'All', label: 'All' }, ...INSTITUTION_TYPES]}
        />
        <FilterSelect
          label="Filter by State:"
          value={stateFilter}
          onChange={setStateFilter}
          options={[{ value: 'All', label: 'All' }, ...stateOptions]}
        />
        <FilterSelect
          label="Filter by Status:"
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_OPTIONS}
        />
      </div>

      {activeFilters.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-800">
          <span className="font-semibold">Applied filters:</span>
          {activeFilters.map((item) => (
            <span
              key={item}
              className="rounded-full border border-green-200 bg-white px-3 py-1 text-xs font-medium text-green-700"
            >
              {item}
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setRefreshTick((value) => value + 1)}
            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      <NlimsDataTable
        key={`institutions-table-${refreshTick}`}
        tableId="institutions-table"
        columns={COLUMNS}
        serverSide
        ajaxUrl="/v1/institutions"
        filters={backendFilters}
        searchQuery={searchQuery}
        hideSearch
        pageLength={10}
        enableRowSelection
        defaultSort={[0, 'asc']}
        onRowAction={handleRowAction}
        onAjaxError={setError}
      />

      {rowMenu && (
        <SystemRowMenu
          rect={rowMenu.rect}
          items={standardRowItems({ isActive: Boolean(rowMenu.row?.is_active) })}
          onClose={() => setRowMenu(null)}
          onPick={(key) => { const row = rowMenu.row; if (key === 'view') setViewRow(row); if (key === 'edit') setEditRow(row); if (key === 'delete') handleDelete(row); if (key === 'toggle-status') handleToggleStatus(row); }}
        />
      )}

      <RecordDetailsModal
        isOpen={!!viewRow}
        onClose={() => setViewRow(null)}
        onEdit={() => {
          setEditRow(viewRow);
          setViewRow(null);
        }}
        onDelete={() => handleDelete(viewRow)}
        deleting={deleting}
        title="Institution Details"
        subtitle={viewRow?.name}
        statusValue={viewRow?.is_active ? 'active' : 'inactive'}
        fields={viewRow ? [
          { label: 'Name', value: viewRow.name || '-' },
          { label: 'Institution Type', value: humanize(viewRow.institution_type) },
          { label: 'State', value: viewRow.state || '-' },
          { label: 'Status', value: viewRow.is_active ? 'Active' : 'Inactive' },
          { label: 'UUID', value: viewRow.uuid, mono: true, wide: true },
        ] : []}
      />

      {createOpen && (
        <CreateInstitutionModal
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreate={handleCreate}
        />
      )}

      {editRow && (
        <EditInstitutionModal
          isOpen={!!editRow}
          onClose={() => setEditRow(null)}
          onSave={handleSave}
          row={editRow}
        />
      )}
    </section>
  );
}
